// src/services/addressService.js
import axios from 'axios';
import { getTenantId } from '../../config/tenantConfig';
import { getValidAuthSession } from '../../utils/authStorage';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

class AddressService {
  constructor() {
    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Api-Key': X_API_KEY,
        'X-Tenant': getTenantId()
      }
    });

    // Add auth token interceptor
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  getAuthToken() {
    return getValidAuthSession()?.token || null;
  }

  // GET all addresses
  async getAddresses() {
    try {
      console.log('📍 Fetching user addresses...');
      const response = await this.apiClient.get('/web/customer/address');
      console.log('✅ Addresses fetched:', response.data);
      return response.data.data || [];
    } catch (error) {
      console.error('❌ Error fetching addresses:', error);
      throw this.handleError(error);
    }
  }

  // GET single address
  async getAddress(addressId) {
    try {
      console.log('📍 Fetching address:', addressId);
      const response = await this.apiClient.get(`/web/customer/address/${addressId}`);
      console.log('✅ Address fetched:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('❌ Error fetching address:', error);
      throw this.handleError(error);
    }
  }

  // POST create new address
  async createAddress(addressData) {
    try {
      console.log('📍 Creating new address:', addressData);

      // Validate required fields (check both zip_code and post_code)
      const hasZip = addressData.zip_code || addressData.post_code;
      const required = ['label', 'address', 'city', 'state'];
      const missing = required.filter(field => !addressData[field]);
      
      if (!hasZip) missing.push('zip_code/post_code');

      if (missing.length > 0) {
        throw new Error(`Missing required fields: ${missing.join(', ')}`);
      }

      const payload = {
        label: addressData.label,
        address: addressData.address,
        apartment: addressData.apartment || '',
        latitude: addressData.latitude || null,
        longitude: addressData.longitude || null,
        city: addressData.city,
        state: addressData.state,
        post_code: addressData.zip_code || addressData.post_code,
        phone: addressData.phone || '',
        recipient_name: addressData.recipient_name || '',
        country: addressData.country || 'India',
        is_default: addressData.is_default || false
      };

      console.log('📤 Sending payload:', payload);
      const response = await this.apiClient.post('/web/customer/address', payload);
      console.log('✅ Address created:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('❌ Error creating address:', error);
      throw this.handleError(error);
    }
  }

  // PUT/PATCH update address
  async updateAddress(addressId, addressData) {
    try {
      console.log('📍 Updating address:', addressId, addressData);

      const payload = {
        label: addressData.label,
        address: addressData.address,
        apartment: addressData.apartment || '',
        latitude: addressData.latitude || null,
        longitude: addressData.longitude || null,
        city: addressData.city,
        state: addressData.state,
        post_code: addressData.zip_code || addressData.post_code,
        phone: addressData.phone || '',
        recipient_name: addressData.recipient_name || '',
        country: addressData.country || 'India',
        is_default: addressData.is_default || false
      };

      console.log('📤 Sending update payload:', payload);
      const response = await this.apiClient.put(`/web/customer/address/${addressId}`, payload);
      console.log('✅ Address updated:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('❌ Error updating address:', error);
      throw this.handleError(error);
    }
  }

  // DELETE address
  async deleteAddress(addressId) {
    try {
      console.log('📍 Deleting address:', addressId);
      const response = await this.apiClient.delete(`/web/customer/address/${addressId}`);
      console.log('✅ Address deleted');
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting address:', error);
      throw this.handleError(error);
    }
  }

  handleError(error) {
    if (error.response) {
      // Server responded with error
      const message = error.response.data?.message || 'Server error occurred';
      return new Error(message);
    } else if (error.request) {
      // No response received
      return new Error('Network error - please check your connection');
    } else {
      // Other errors
      return error;
    }
  }
}

export default new AddressService();